import { useState, useRef } from 'react';
import { useMedia } from 'react-use';
import { ProductCardSkeleton } from '~/shared/components';
import { CarouselHandle, CarouselDrag } from '~/shared/components/CarouselDrag';
import { Bounds } from '~/shared/components/CarouselDrag/CarouselDrag';
import { useCardsWidth } from '~/shared/components/CarouselDrag/hooks/useCardsWidth';
import { Visibility } from '~/shared/utils/jsx';
import { useTranslation } from '~/shared/utils';
import {
    StyledPagination,
    StyledNavigationButton,
    StyledCarouselRef,
    StyledCarouselContent,
    StyledCarouselItem,
} from './styled';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import { ModuleHeader } from '~/templates/blocks/components/ModuleHeader';
import { queries } from '~/theme';
import {
    Link,
    M30ProductCarouselModule,
    M31ProductCarouselModule,
    M32ProductCarouselModule,
} from '~/lib/data-contract';
import ChevronLeft from '$icons/chevron-left.svg';
import ChevronRight from '$icons/chevron-right.svg';
import { HitProductCard } from '../HitProductCard';
import { AlgoliaHit } from '../../models';
import { useModule } from '~/templates';
import { config } from '../../constants';

export type Props = {
    hits?: AlgoliaHit[];
};

export const HitCarousel = ({ hits }: Props) => {
    const { cardWidth } = useCardsWidth();
    const [bounds, setBounds] = useState<Bounds | null>(null);
    const { translate } = useTranslation();
    const {
        element: { headline, link, maxNumberItems },
    } = useModule<M30ProductCarouselModule | M31ProductCarouselModule | M32ProductCarouselModule>();

    // Purely used to get the perceived module width.
    // Cant calculate based on carousel since it's edge to edge
    const moduleWidthRef = useRef<HTMLDivElement>(null);
    const carouselRef = useRef<CarouselHandle>(null);
    const isMobile = !useMedia(queries.sm, false);

    return (
        <>
            <ModuleContainer hasGutter spacingTop="none" spacingBottom="none">
                <ModuleHeader
                    headline={headline}
                    link={link as Link}
                    variant={isMobile ? 'display3' : 'display2'}
                >
                    <Visibility.VisibleFrom md>
                        <StyledPagination>
                            <StyledNavigationButton
                                onClick={() => carouselRef.current?.goRight()}
                                disabled={bounds?.startReached}
                                variant="tertiary"
                                size="md"
                                shape="icon"
                                shade="dark"
                                showHoverIndicator={false}
                                children={<ChevronLeft title={translate('general.scrollLeft')} />}
                            />

                            <StyledNavigationButton
                                onClick={() => carouselRef.current?.goLeft()}
                                disabled={bounds?.endReached}
                                variant="tertiary"
                                size="md"
                                shape="icon"
                                shade="dark"
                                showHoverIndicator={false}
                                children={<ChevronRight title={translate('general.scrollRight')} />}
                            />
                        </StyledPagination>
                    </Visibility.VisibleFrom>
                </ModuleHeader>
            </ModuleContainer>

            <StyledCarouselRef cardWidth={cardWidth} ref={moduleWidthRef}>
                <CarouselDrag
                    ref={carouselRef}
                    onBoundsChanged={setBounds}
                    customScroll={true}
                    hasGutter={true}
                >
                    <StyledCarouselContent>
                        {!hits?.length
                            ? [...Array(maxNumberItems || config.maxNumberItemsDefault).keys()].map(
                                  (index) => (
                                      <StyledCarouselItem key={index}>
                                          <ProductCardSkeleton shimmer={true} fillHeight insideScroll/>
                                      </StyledCarouselItem>
                                  )
                              )
                            : hits.map((product, index) => (
                                  <StyledCarouselItem key={`${product.productUrl}-${index}`}>
                                      <HitProductCard hit={product} />
                                  </StyledCarouselItem>
                   
                              ))}
                    </StyledCarouselContent>
                </CarouselDrag>
            </StyledCarouselRef>
        </>
    );
};
